// Pure transitions for a chat tab strip, shared by the scene and DS reducers.
// Each returns the whole next state of the strip — tabs, active id, and whether
// the draft goes with it — so a reducer can apply a tab operation in one step.

// A tab as the strip renders it: an id resolved to the title to show.
export interface ChatTab {
	id: string;
	title: string;
	isDraft: boolean;
}

export interface ChatTabTransition {
	tabs: string[];
	activeChatId: string | null;
	// true when the operation removed the tab the draft was living in
	discardDraft: boolean;
}

// Every open chat has a tab, so activating a chat that has none opens one.
export const withChatTab = (tabs: string[], chatId: string | null): string[] => {
	if (!chatId || tabs.includes(chatId)) return tabs;
	return [...tabs, chatId];
};

// Closing the last tab is refused: the panel always shows a chat, so it never
// needs an empty state. Closing the active tab moves to its right-hand
// neighbour, or the new last tab when it was the rightmost.
export const closeChatTab = (
	tabs: string[],
	activeChatId: string | null,
	draftChatId: string | null,
	closedId: string
): ChatTabTransition => {
	const index = tabs.indexOf(closedId);
	if (index === -1 || tabs.length <= 1) {
		return { tabs, activeChatId, discardDraft: false };
	}

	const remaining = tabs.filter((id) => id !== closedId);
	return {
		tabs: remaining,
		activeChatId:
			closedId === activeChatId
				? remaining[Math.min(index, remaining.length - 1)]
				: activeChatId,
		discardDraft: closedId === draftChatId,
	};
};

export const closeOtherChatTabs = (
	tabs: string[],
	draftChatId: string | null,
	keptId: string
): ChatTabTransition => {
	if (!tabs.includes(keptId)) {
		return { tabs, activeChatId: keptId, discardDraft: false };
	}
	return {
		tabs: [keptId],
		activeChatId: keptId,
		discardDraft: draftChatId !== null && draftChatId !== keptId,
	};
};

// Swaps a tab's chat for a fresh draft. Only one draft exists at a time, so an
// existing draft is reused — its tab absorbs the target, which just closes.
export const replaceChatTabWithDraft = (
	tabs: string[],
	existingDraftId: string | null,
	targetId: string,
	newDraftId: string
): { tabs: string[]; activeChatId: string; useExistingDraft: boolean } => {
	if (existingDraftId && existingDraftId !== targetId) {
		return {
			tabs: withChatTab(
				tabs.filter((id) => id !== targetId),
				existingDraftId
			),
			activeChatId: existingDraftId,
			useExistingDraft: true,
		};
	}
	return {
		tabs: tabs.map((id) => (id === targetId ? newDraftId : id)),
		activeChatId: newDraftId,
		useExistingDraft: false,
	};
};
